/** Reusable approved PDP: local component, no cart writes or message sending. */
if (!customElements.get('tplx-pdp')) {
  class TplxPdp extends HTMLElement {
    /** @type {AbortController | null} */
    #controller = null;
    /** @type {HTMLAnchorElement | null} */
    #variant = null;
    /** @type {HTMLInputElement | null} */
    #quantity = null;

    connectedCallback() {
      if (this.#controller) return;
      this.#quantity = this.querySelector('[data-quantity]');
      this.#variant = this.querySelector('[data-variant-id][aria-current="true"]');
      this.#controller = new AbortController();
      const options = { signal: this.#controller.signal };
      for (const thumbnail of this.querySelectorAll('[data-media-id]')) {
        const selected = thumbnail.getAttribute('aria-current') === 'true' || thumbnail.getAttribute('aria-pressed') === 'true';
        thumbnail.removeAttribute('aria-current');
        thumbnail.setAttribute('role', 'button');
        thumbnail.setAttribute('aria-pressed', String(selected));
        thumbnail.addEventListener('click', (event) => {
          event.preventDefault();
          this.#showImage(thumbnail);
        }, options);
        thumbnail.addEventListener('keydown', (event) => {
          if (event.key !== ' ') return;
          event.preventDefault();
          this.#showImage(thumbnail);
        }, options);
        thumbnail.addEventListener('focus', () => this.#showImage(thumbnail), options);
        thumbnail.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          if (!matchMedia('(any-hover: hover) and (any-pointer: fine)').matches) return;
          this.#showImage(thumbnail);
        }, options);
      }
      this.querySelector('.tplx-pdp__image')?.addEventListener('error', () => {
        const caption = this.querySelector('[data-gallery-caption]');
        if (caption && this.dataset.mediaError) caption.textContent = this.dataset.mediaError;
      }, options);
      for (const variant of this.querySelectorAll('[data-variant-id]')) {
        variant.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.#selectVariant(variant);
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.dataset.variantId);
          history.replaceState(history.state, '', url);
        }, options);
      }
      window.addEventListener('popstate', () => {
        const variantId = new URL(location.href).searchParams.get('variant');
        const variants = [...this.querySelectorAll('[data-variant-id]')];
        const variant = variants.find((item) => item.dataset.variantId === variantId) || variants[0];
        if (variant) this.#selectVariant(variant);
      }, options);
      // Gallery and variant selection also work when the quantity UI is absent.
      if (!this.#quantity) return;
      for (const button of this.querySelectorAll('[data-quantity-change]')) {
        button.hidden = false;
        button.addEventListener('click', () => {
          const min = Number(this.#quantity.min) || 1;
          const step = Number(this.#quantity.step) || 1;
          const value = this.#validQuantity() ? Number(this.#quantity.value) : min;
          const max = this.#quantity.max ? Number(this.#quantity.max) : Number.MAX_SAFE_INTEGER;
          this.#quantity.value = String(Math.max(min, Math.min(max, value + Number(button.dataset.quantityChange) * step)));
          this.#updateInquiry();
        }, options);
      }
      this.#quantity.addEventListener('input', () => this.#updateInquiry(), options);
      this.#quantity.addEventListener('change', () => this.#updateInquiry(), options);
      for (const link of this.querySelectorAll('[data-inquiry]')) {
        link.addEventListener('click', (event) => {
          this.#updateInquiry();
          if (!link.hasAttribute('href')) {
            event.preventDefault();
            this.#quantity.focus();
          }
        }, options);
      }
      this.#updateInquiry();
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#controller = null;
    }

    /** @param {HTMLAnchorElement} thumbnail */
    #showImage(thumbnail) {
      const image = this.querySelector('.tplx-pdp__image');
      if (!image || !thumbnail.dataset.imageSrc || thumbnail.getAttribute('aria-pressed') === 'true') return;
      if (thumbnail.dataset.imageAlt !== undefined) image.alt = thumbnail.dataset.imageAlt;
      if (Number(thumbnail.dataset.imageWidth) > 0) image.width = Number(thumbnail.dataset.imageWidth);
      if (Number(thumbnail.dataset.imageHeight) > 0) image.height = Number(thumbnail.dataset.imageHeight);
      image.srcset = thumbnail.dataset.imageSrcset || '';
      image.src = thumbnail.dataset.imageSrc;
      for (const item of this.querySelectorAll('[data-media-id]')) {
        item.setAttribute('aria-pressed', String(item === thumbnail));
      }
      const caption = this.querySelector('[data-gallery-caption]');
      if (caption) caption.textContent = thumbnail.dataset.imageAlt ?? image.alt;
    }

    /** @param {HTMLAnchorElement} variant */
    #selectVariant(variant) {
      this.#variant = variant;
      for (const item of this.querySelectorAll('[data-variant-id]')) {
        if (item === variant) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      }
      const selectedVariant = this.querySelector('[data-selected-variant]');
      if (selectedVariant) selectedVariant.value = variant.dataset.variantId;
      const price = this.querySelector('[data-current-price]');
      if (price && variant.dataset.price !== undefined) price.textContent = variant.dataset.price;
      const title = this.querySelector('[data-current-variant]');
      if (title && variant.dataset.variantTitle !== undefined) title.textContent = variant.dataset.variantTitle;
      const sku = this.querySelector('[data-current-sku]');
      if (sku) sku.textContent = variant.dataset.sku || '';
      if (this.#quantity) {
        this.#quantity.min = variant.dataset.quantityMin || '1';
        this.#quantity.step = variant.dataset.quantityStep || '1';
        this.#quantity.max = variant.dataset.quantityMax || '';
        if (!this.#validQuantity()) this.#quantity.value = this.#quantity.min;
      }
      const media = [...this.querySelectorAll('[data-media-id]')].find((item) => item.dataset.mediaId === variant.dataset.featuredMediaId);
      if (media) this.#showImage(media);
      this.#updateInquiry();
    }

    /** @returns {boolean} */
    #validQuantity() {
      if (!this.#quantity) return false;
      const value = Number(this.#quantity.value);
      return Number.isSafeInteger(value) && value > 0 && this.#quantity.checkValidity();
    }

    #updateInquiry() {
      if (!this.#quantity) return;
      const valid = this.#validQuantity();
      const error = this.querySelector('[data-quantity-error]');
      if (error) error.hidden = valid;
      this.#quantity.setAttribute('aria-invalid', String(!valid));
      const quantity = Number(this.#quantity.value);
      const min = Number(this.#quantity.min) || 1;
      const step = Number(this.#quantity.step) || 1;
      const max = this.#quantity.max ? Number(this.#quantity.max) : Number.MAX_SAFE_INTEGER;
      for (const button of this.querySelectorAll('[data-quantity-change]')) {
        button.disabled = valid && (Number(button.dataset.quantityChange) < 0 ? quantity - step < min : quantity + step > max);
      }
      const links = this.querySelectorAll('[data-inquiry]');
      if (!valid) {
        for (const link of links) {
          link.removeAttribute('href');
          link.setAttribute('aria-disabled', 'true');
          link.setAttribute('role', 'link');
          link.tabIndex = 0;
        }
        return;
      }
      if (!this.#variant || !links.length) return;
      const variant = this.#variant.dataset;
      const inquiryUrl = this.dataset.inquiryUrl || this.dataset.contactUrl;
      if (!inquiryUrl) return;
      let url;
      try {
        url = new URL(inquiryUrl, location.origin);
      } catch {
        return;
      }
      if (this.dataset.inquiryMode === 'whatsapp') {
        if (!this.dataset.messageTemplate) return;
        const replacements = { '__VARIANT__': variant.variantTitle || '', '__QUANTITY__': String(quantity), '__SKU__': variant.sku || '', '__PRICE__': variant.priceLabel || '' };
        let message = this.dataset.messageTemplate;
        for (const [marker, value] of Object.entries(replacements)) message = message.split(marker).join(value);
        url.searchParams.set('text', message);
      } else {
        url.searchParams.set('product', this.dataset.productTitle || '');
        url.searchParams.set('product_id', this.dataset.productId || '');
        url.searchParams.set('variant_id', variant.variantId || '');
        url.searchParams.set('variant', variant.variantTitle || '');
        url.searchParams.set('sku', variant.sku || '');
        url.searchParams.set('quantity', String(quantity));
      }
      for (const link of links) {
        link.href = url.toString();
        link.removeAttribute('aria-disabled');
        link.removeAttribute('role');
        link.removeAttribute('tabindex');
      }
      this.dispatchEvent(new CustomEvent('tplx-pdp:selection', {
        bubbles: true,
        detail: { productId: this.dataset.productId, variantId: variant.variantId, variant: variant.variantTitle, sku: variant.sku, quantity, inquiryUrl: url.toString() }
      }));
    }
  }
  customElements.define('tplx-pdp', TplxPdp);
}
